import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, Mountain, Users, Calendar, FolderOpen, 
  BookOpen, Images, Bed, MessageSquare, Heart, Settings,
  LogOut, Menu, X, Search, Plus, Edit, Trash2, Upload, Save,
  Mail, MailOpen, Clock, CheckCheck
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SIDEBAR_ITEMS = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/admin' },
  { icon: Mountain, label: 'Falesie', path: '/admin/crags' },
  { icon: Users, label: 'Team', path: '/admin/team' },
  { icon: Calendar, label: 'Eventi', path: '/admin/events' },
  { icon: FolderOpen, label: 'Progetti', path: '/admin/projects' },
  { icon: BookOpen, label: 'Blog', path: '/admin/blog' },
  { icon: Images, label: 'Gallery', path: '/admin/gallery' },
  { icon: Bed, label: 'Alloggi', path: '/admin/accommodations' },
  { icon: MessageSquare, label: 'Messaggi', path: '/admin/messages' },
  { icon: Heart, label: 'Donazioni', path: '/admin/donations' },
  { icon: Search, label: 'SEO', path: '/admin/seo' },
  { icon: Upload, label: 'Uploads', path: '/admin/uploads' },
  { icon: Settings, label: 'Impostazioni', path: '/admin/settings' },
];

function AdminSidebar({ currentPath, sidebarOpen, setSidebarOpen, logout }) {
  return (
    <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-[#0A0A0A] transform transition-transform duration-300 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
      <div className="flex items-center justify-between h-16 px-6 border-b border-gray-800">
        <Link to="/" className="text-white font-heading text-lg uppercase">CMS Admin</Link>
        <button className="lg:hidden text-white" onClick={() => setSidebarOpen(false)}><X className="w-6 h-6" /></button>
      </div>
      <nav className="p-4 space-y-1 max-h-[calc(100vh-8rem)] overflow-y-auto">
        {SIDEBAR_ITEMS.map((item) => (
          <Link key={item.path} to={item.path} className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${currentPath === item.path ? 'bg-[#FF6D00] text-white' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}>
            <item.icon className="w-5 h-5" /><span className="text-sm">{item.label}</span>
          </Link>
        ))}
      </nav>
      <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-800">
        <button onClick={logout} className="flex items-center gap-3 px-4 py-3 text-gray-400 hover:text-white w-full">
          <LogOut className="w-5 h-5" /><span className="text-sm">Logout</span>
        </button>
      </div>
    </aside>
  );
}

export function AdminMessages() {
  const { logout, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [filter, setFilter] = useState('all'); // all, unread

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) { navigate('/admin/login'); return; }
    fetchMessages();
  }, [isAuthenticated, authLoading, navigate, filter]);

  const fetchMessages = async () => {
    try { 
      const url = filter === 'unread' 
        ? `${API}/contact/messages?unread_only=true` 
        : `${API}/contact/messages`;
      const res = await axios.get(url); 
      setMessages(res.data); 
    }
    catch (error) { toast.error('Errore nel caricamento'); }
    finally { setLoading(false); }
  };

  const markAsRead = async (message) => {
    if (message.is_read) return;
    try {
      await axios.put(`${API}/contact/messages/${message.id}/read`);
      fetchMessages();
    } catch (error) { 
      console.error('Error marking as read:', error);
    }
  };

  const openMessage = (message) => {
    setSelectedMessage(message);
    setShowDialog(true);
    markAsRead(message);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins} min fa`;
    if (diffHours < 24) return `${diffHours} ore fa`;
    if (diffDays < 7) return `${diffDays} giorni fa`;
    return date.toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const unreadCount = messages.filter(m => !m.is_read).length;

  if (authLoading || !isAuthenticated) return null;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-messages">
      <AdminSidebar currentPath="/admin/messages" sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} logout={logout} />
      <div className="lg:ml-64">
        <header className="bg-white border-b h-16 flex items-center justify-between px-6">
          <button className="lg:hidden" onClick={() => setSidebarOpen(true)}><Menu className="w-6 h-6" /></button>
          <div className="flex items-center gap-4">
            <h1 className="font-heading text-xl uppercase">Messaggi</h1>
            {unreadCount > 0 && (
              <span className="px-2 py-1 bg-[#FF6D00] text-white text-xs rounded-full">
                {unreadCount} nuovi
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <Button 
              variant={filter === 'all' ? 'default' : 'outline'} 
              size="sm" 
              onClick={() => setFilter('all')}
            >
              Tutti
            </Button>
            <Button 
              variant={filter === 'unread' ? 'default' : 'outline'} 
              size="sm" 
              onClick={() => setFilter('unread')}
            >
              Non letti
            </Button>
          </div>
        </header>

        <main className="p-6">
          {loading ? (
            <div className="flex justify-center py-20"><div className="animate-spin w-8 h-8 border-4 border-[#FF6D00] border-t-transparent rounded-full" /></div>
          ) : messages.length === 0 ? (
            <div className="text-center py-20">
              <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">
                {filter === 'unread' ? 'Nessun messaggio non letto' : 'Nessun messaggio ricevuto'}
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-lg border overflow-hidden">
              {messages.map((message) => (
                <div 
                  key={message.id} 
                  onClick={() => openMessage(message)}
                  className={`flex items-start gap-4 p-4 border-b cursor-pointer hover:bg-gray-50 transition-colors ${!message.is_read ? 'bg-orange-50' : ''}`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${!message.is_read ? 'bg-[#FF6D00] text-white' : 'bg-gray-100 text-gray-400'}`}>
                    {!message.is_read ? <Mail className="w-5 h-5" /> : <MailOpen className="w-5 h-5" />}
                  </div>
                  
                  <div className="flex-grow min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`font-medium ${!message.is_read ? 'text-black' : 'text-gray-700'}`}>
                        {message.name}
                      </span>
                      {!message.is_read && (
                        <span className="w-2 h-2 bg-[#FF6D00] rounded-full"></span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 mb-1">{message.email}</p>
                    {message.subject && (
                      <p className={`text-sm mb-1 ${!message.is_read ? 'font-medium' : 'text-gray-700'}`}>
                        {message.subject}
                      </p>
                    )}
                    <p className="text-sm text-gray-500 truncate">{message.message}</p>
                  </div>
                  
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    <span className="text-xs text-gray-400 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatDate(message.created_at)}
                    </span>
                    {message.is_read && (
                      <CheckCheck className="w-4 h-4 text-green-500" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </main>
      </div>

      {/* Message Detail Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Messaggio da {selectedMessage?.name}</DialogTitle>
          </DialogHeader>
          {selectedMessage && (
            <div className="py-4">
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Da:</span>
                    <p className="font-medium">{selectedMessage.name}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Email:</span>
                    <p>
                      <a href={`mailto:${selectedMessage.email}`} className="text-[#FF6D00] hover:underline">
                        {selectedMessage.email}
                      </a>
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500">Data:</span>
                    <p>{new Date(selectedMessage.created_at).toLocaleString('it-IT')}</p>
                  </div>
                  {selectedMessage.subject && (
                    <div>
                      <span className="text-gray-500">Oggetto:</span>
                      <p className="font-medium">{selectedMessage.subject}</p>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="border-t pt-4">
                <h3 className="text-sm font-medium text-gray-500 mb-2">Messaggio:</h3>
                <div className="prose prose-sm max-w-none">
                  <p className="whitespace-pre-wrap">{selectedMessage.message}</p>
                </div>
              </div>
              
              <div className="flex justify-end gap-2 mt-6">
                <Button variant="outline" onClick={() => setShowDialog(false)}>
                  Chiudi
                </Button>
                <Button 
                  className="btn-primary"
                  onClick={() => window.location.href = `mailto:${selectedMessage.email}?subject=Re: ${selectedMessage.subject || 'Messaggio da Caltavuturo Climbing'}`}
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Rispondi
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
